import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { useUserStore } from '../../stores/userStore';
import { albumPath } from '../../components/content/albumPath';
const prefix = 'POSTPOP_WEB_RECENT_CONTENT_V1_';
const cache = new Map();
const listeners = new Set();
const empty = [];
export const recentScope = (userId) => (userId ? `user_${userId}` : 'guest');
const valid = (item) =>
  item &&
  ['album', 'review', 'playlist'].includes(item.kind) &&
  typeof item.id === 'string' &&
  typeof item.title === 'string' &&
  typeof item.path === 'string' &&
  /^\/(reviews|lists)\/[^/]/.test(item.path) &&
  !Array.from(item.path).some((character) => character === '\\' || character.charCodeAt(0) < 32) &&
  Number.isFinite(item.viewedAt);
export function readRecent(scope) {
  if (cache.has(scope)) return cache.get(scope);
  let items = empty;
  try {
    const saved = JSON.parse(localStorage.getItem(prefix + scope));
    if (Array.isArray(saved)) items = saved.filter(valid).slice(0, 12);
  } catch {
    /* Blocked/malformed storage falls back to this session. */
  }
  cache.set(scope, items);
  return items;
}
function writeRecent(scope, items) {
  cache.set(scope, items);
  try {
    localStorage.setItem(prefix + scope, JSON.stringify(items));
  } catch {
    /* Session fallback. */
  }
  listeners.forEach((listener) => listener());
}
export function recordRecent(scope, item) {
  const next = { ...item, viewedAt: Date.now() };
  if (!valid(next)) return;
  writeRecent(scope, [next, ...readRecent(scope).filter((old) => old.id !== next.id)].slice(0, 12));
}
export function removeRecent(scope, predicate) {
  const before = readRecent(scope),
    after = before.filter((item) => !predicate(item));
  if (after.length !== before.length) writeRecent(scope, after);
}
export function removeUnavailableRecent(scope, path, error) {
  if ([404, 410].includes(error?.response?.status))
    removeRecent(scope, (item) => item.path === path);
}
function subscribe(listener) {
  listeners.add(listener);
  const sync = (event) => {
    if (event.key === null) cache.clear();
    else if (event.key?.startsWith(prefix)) cache.delete(event.key.slice(prefix.length));
    else return;
    listener();
  };
  window.addEventListener('storage', sync);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', sync);
  };
}
export function useRecentContent() {
  const userId = useUserStore((state) => state.user?.id),
    scope = recentScope(userId);
  const snapshot = useCallback(() => readRecent(scope), [scope]);
  return { scope, items: useSyncExternalStore(subscribe, snapshot, () => empty) };
}
export function useRememberContent(item, { ready = true, error, path } = {}) {
  const userId = useUserStore((state) => state.user?.id),
    scope = recentScope(userId);
  const location = useLocation(),
    last = useRef(null);
  useEffect(() => {
    removeUnavailableRecent(scope, path || location.pathname, error);
    if (!ready || !item || error) return;
    const visit = `${scope}:${location.key}:${item.id}`;
    if (last.current === visit) return;
    last.current = visit;
    recordRecent(scope, item);
  }, [scope, item, ready, error, path, location.key, location.pathname]);
}
export function recentAlbum(album) {
  if (!album?.title || !(album.id || album.slug || album.sourceId)) return null;
  return {
    id: `album:${album.id || album.slug || `${album.albumSource}:${album.sourceId}`}`,
    kind: 'album',
    title: album.title,
    contentRating: album.contentRating,
    subtitle: album.artist?.name,
    imageUrl: album.imageUrl,
    path: albumPath(album),
  };
}
export function recentReview(review, album) {
  const item = recentAlbum(album);
  if (!review?.id || !item) return null;
  return {
    ...item,
    id: `review:${review.id}`,
    kind: 'review',
    subtitle: review.user?.nickname,
    path: `${item.path}/user-reviews/${encodeURIComponent(review.id)}`,
  };
}
export function recentPlaylist(playlist) {
  if (!playlist?.title || !(playlist.slug || playlist.id)) return null;
  return {
    id: `playlist:${playlist.id || playlist.slug}`,
    kind: 'playlist',
    title: playlist.title,
    subtitle: playlist.username,
    imageUrl: playlist.imageUrl,
    imageUrls: [
      ...new Set(
        (
          playlist.previewImageUrls ||
          [...(playlist.albums || []), ...(playlist.tracks || [])]
            .sort((a, b) => a.position - b.position)
            .map((item) => item.albumImageUrl)
        ).filter(Boolean),
      ),
    ].slice(0, 4),
    path: `/lists/${encodeURIComponent(playlist.slug || playlist.id)}`,
  };
}
